import React, { useCallback, useContext, useMemo, useState } from 'react';

import { plus } from '@/assets/icons';
import { LayoutContext } from '@/components/Layout';
import convertSetlitToOpenSetlistConfig from '@/components/Layout/components/GenerateSetlist/helpers/convertSetlitToOpenSetlistConfig';
import { NewMessage } from '@/components/NewMessage';
import { SetlistSummaryCard } from '@/components/SetlistSummaryCard';
import { EmptyResourceMessage } from '@/components/shared/EmptyResourceMessage';
import { SetlistPermissionSheet } from '@/components/shared/SetlistPermissionSheet';
import { SheetLayout } from '@/components/shared/SheetLayout';
import { TeamMilestones, UserMilestones } from '@/config/constants';
import { setlistsUrl } from '@/config/urls';
import { Permissions } from '@/graphql';
import { classNames } from '@/helpers/classNames';
import { useFormattedTitle } from '@/hooks/useFormattedTitle';
import {
	useAnalyticsEvent,
	useFireAnalyticsPageView,
} from '@/providers/AnalyticsProvider';
import { useAuth } from '@/providers/AuthProvider';
import { useLayout } from '@/providers/LayoutProvider';
import { usePermissions } from '@/providers/PermissionsProvider';
import { useServices } from '@/providers/ServicesProvider';
import { useSetlists } from '@/providers/SetlistsProvider';
import { useTeam } from '@/providers/TeamProvider';
import { useTeamSongs } from '@/providers/TeamSongsProvider';
import { Helmet } from 'react-helmet';

import { SetlistsFilterBar } from './components/SetlistsFilterBar';
import styles from './Setlists.module.scss';

export const Setlists: React.FC = () => {
	const { openSetlistWithCallback, openSetlistGenerate, openQuickAddSongs } =
		useLayout();
	const { teamSongs } = useTeamSongs();
	const { hasPermission } = usePermissions();
	const { hasMilestones, team } = useTeam();
	const { setlists, isLoading } = useSetlists();
	const { services } = useServices();
	const { user: currentUser } = useAuth();

	const [permissionSheetOpen, setPermissionSheetOpen] = useState(false);
	const [serviceFilter, setServiceFilter] = useState<string[]>([]);
	const [creatorFilter, setCreatorFilter] = useState<string[]>([]);
	const [newMessageOpen, setNewMessageOpen] = useState(false);

	const [title] = useFormattedTitle('Asaph | Setlists');

	const fireOpenSetlistGenerate = useAnalyticsEvent(
		'Layout_Open_Generate_Settings_Bottom_Panel',
	);

	useFireAnalyticsPageView({
		enabled: !!title,
		pageViewArgs: {
			title,
		},
	});

	const handleSetlistGenerateClick = useCallback(() => {
		if (!hasPermission(Permissions.TeamUpdate)) {
			setPermissionSheetOpen(true);
			return;
		}
		fireOpenSetlistGenerate({ url: setlistsUrl });
		openSetlistGenerate();
	}, [openSetlistGenerate, fireOpenSetlistGenerate, hasPermission]);

	const handleServiceToggle = useCallback((value: string) => {
		setServiceFilter((prev) =>
			prev.includes(value)
				? prev.filter((v) => v !== value)
				: [...prev, value],
		);
	}, []);

	const handleCreatorToggle = useCallback((value: string) => {
		setCreatorFilter((prev) =>
			prev.includes(value)
				? prev.filter((v) => v !== value)
				: [...prev, value],
		);
	}, []);

	const handleClearFilters = useCallback(() => {
		setServiceFilter([]);
		setCreatorFilter([]);
	}, []);

	const serviceOptions = useMemo(
		() => services.map((s) => ({ label: s.name, value: s.id })),
		[services],
	);

	const creatorOptions = useMemo(() => {
		const memberMap = new Map(
			team.members.map((m) => [
				m.user.id,
				m.user.displayName || m.user.email,
			]),
		);
		const uniqueCreatorIds = Array.from(
			new Set(setlists.map((s) => s.userId)),
		);
		return uniqueCreatorIds
			.filter((id) => memberMap.has(id))
			.map((id) => ({
				label:
					id === currentUser.id
						? `${memberMap.get(id)!} (you)`
						: memberMap.get(id)!,
				value: id,
			}))
			.sort((a, b) => {
				if (a.value === currentUser.id) return -1;
				if (b.value === currentUser.id) return 1;
				return 0;
			});
	}, [setlists, team.members, currentUser.id]);

	const filteredSetlists = useMemo(() => {
		let result = setlists;
		if (serviceFilter.length > 0) {
			result = result.filter(
				(s) =>
					s.serviceId != null && serviceFilter.includes(s.serviceId),
			);
		}
		if (creatorFilter.length > 0) {
			result = result.filter((s) => creatorFilter.includes(s.userId));
		}
		return result;
	}, [setlists, serviceFilter, creatorFilter]);

	const noResources = !setlists.length;
	const noSongs = !hasMilestones([TeamMilestones.ADDED_INITIAL_SONGS]);

	return (
		<>
			<Helmet title={title} />
			<SheetLayout
				titleComponent="Setlists"
				actions={[
					{
						icon: plus,
						onClick: handleSetlistGenerateClick,
					},
				]}
				isLoading={isLoading}
				topComponent={
					<SetlistsFilterBar
						serviceFilter={serviceFilter}
						onServiceToggle={handleServiceToggle}
						serviceOptions={
							services.length > 1 ? serviceOptions : undefined
						}
						creatorFilter={creatorFilter}
						onCreatorToggle={handleCreatorToggle}
						creatorOptions={
							creatorOptions.length > 1
								? creatorOptions
								: undefined
						}
						onClearAll={handleClearFilters}
					/>
				}
				// bottomPanelOpen={isSettingsPanelOpen}
				// bottomPanelContent={
				// 	<GenerateSettingsBottomPanel
				// 		onSave={(generateSettings, generateOnOpen) => {
				// 			setIsSettingsPanelOpen(false);
				// 			openSetlistWithCallback(() => {}, {
				// 				editOnOpen: true,
				// 				generateSettings,
				// 				generateOnOpen,
				// 			});
				// 		}}
				// 	/>
				// }
				// onBottomPanelClose={() => setIsSettingsPanelOpen(false)}
			>
				<div
					className={classNames([
						styles['songs'],
						(noResources || !filteredSetlists.length) &&
							styles['full-height'],
					])}
				>
					{noResources ? (
						hasPermission(Permissions.TeamUpdate) ? (
							noSongs ? (
								<EmptyResourceMessage
									onActionClick={openQuickAddSongs}
									title="Add 10 songs first"
									subtitle={
										teamSongs.length
											? `Keep going! You've added ${teamSongs.length} so far.`
											: 'So you can create a setlist'
									}
									actionLabel="Add Songs"
								/>
							) : (
								<EmptyResourceMessage
									onActionClick={() => openSetlistGenerate()}
									title="You have no setlists"
									subtitle="Create a setlist now"
									actionLabel="Create Setlist"
								/>
							)
						) : (
							<EmptyResourceMessage
								title="There are no setlists"
								subtitle="Only admins can create setlists. Message an admin to upgrade your permissions."
								actionLabel="New Message"
								onActionClick={() => setNewMessageOpen(true)}
							/>
						)
					) : (
						<>
							{filteredSetlists.length ? (
								<div>
									{filteredSetlists.map((setlist, si) => {
										return (
											<SetlistSummaryCard
												size="md"
												title="Test"
												setlistId={setlist.id}
												setlist={setlist}
												timeClassName={
													styles[
														'setlist-summary-time'
													]
												}
												className={classNames([
													si ===
														filteredSetlists.length -
															1 && styles['last'],
													styles[
														'setlist-summary-card'
													],
												])}
												onClick={() => {
													openSetlistWithCallback(
														() => {},
														convertSetlitToOpenSetlistConfig(
															setlist,
														),
													);
												}}
											/>
										);
									})}
								</div>
							) : (
								<EmptyResourceMessage
									onActionClick={handleClearFilters}
									title="No setlists"
									subtitle="Your filters have returned 0 results"
									actionLabel="Clear filters"
								/>
							)}
						</>
					)}
				</div>
			</SheetLayout>
			<SetlistPermissionSheet
				open={permissionSheetOpen}
				onClose={() => setPermissionSheetOpen(false)}
				onNewMessage={() => {
					setPermissionSheetOpen(false);
					setNewMessageOpen(true);
				}}
			/>
			<NewMessage
				open={newMessageOpen}
				handleClose={() => setNewMessageOpen(false)}
				initialMessage
			/>
		</>
	);
};
